import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { eur } from "../api";
import { usePageTitle, MedicalWarning } from "../components/common";
import { CONSENTS, AVERTISSEMENT } from "../content";

export default function Checkout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  let prep = location.state?.prep || null;
  if (!prep) {
    try { prep = JSON.parse(sessionStorage.getItem(`prep_${slug}`) || "null"); } catch { prep = null; }
  }
  const [service, setService] = useState(null);
  const [type, setType] = useState("particulier");
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState({
    first_name: "", last_name: "", company_name: "", contact_first_name: "",
    contact_last_name: "", email: "", siren: "", vat_number: "", billing_address: "",
    address_complement: "", postal_code: "", city: "", country: "France",
  });
  const [consents, setConsents] = useState({
    nature_spirituelle: false, cgv: false, majeur: false, execution_immediate: false,
  });
  usePageTitle(service ? `Commander — ${service.name}` : "Commander");

  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => {
      setService(r.data);
      if (r.data.category === "guidance" && !prep) {
        toast.error("Merci de remplir ton formulaire de guidance.");
        navigate(`/guidances/${slug}`);
        return;
      }
      if (prep) setBilling((b) => ({ ...b, first_name: prep.first_name || "", last_name: prep.last_name || "" }));
    }).catch(() => navigate("/guidances"));
  }, [slug]);

  const set = (k) => (e) => setBilling({ ...billing, [k]: e.target.value });
  const allConsents = Object.values(consents).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!allConsents) {
      toast.error("Merci de cocher tous les consentements pour continuer.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        service_slug: slug,
        billing: { ...billing, customer_type: type },
        consents,
        prep,
        origin_url: window.location.origin,
      });
      try { sessionStorage.removeItem(`prep_${slug}`); } catch {}
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'initier le paiement.");
      setLoading(false);
    }
  };

  if (!service) return <div className="container-x py-24 text-center text-[#7A5D58]">Chargement…</div>;

  return (
    <div className="container-x py-10 sm:py-16 fade-up">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[#7A5D58] hover:text-[#5A3A4B] mb-8">
        <ArrowLeft size={16} /> Retour
      </button>

      <div className="grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 space-y-6" data-testid="checkout-form">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-medium text-[#5A3A4B]">Informations de facturation</h1>
            <p className="mt-2 text-sm text-[#7A5D58]">
              Ces informations servent uniquement à ta facture. Ta question et ta photo seront demandées après le paiement, en toute confidentialité.
            </p>
          </div>

          {/* Customer type */}
          <div className="flex gap-3">
            {["particulier", "professionnel"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                data-testid={`customer-type-${t}`}
                className={`flex-1 rounded-xl border px-4 py-3 text-sm font-medium capitalize transition ${
                  type === t ? "border-[#5A3A4B] bg-[#5A3A4B] text-[#FCFAF7]" : "border-[#7A5D58]/25 text-[#5A3A4B]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="card-soft p-6 space-y-4">
            {type === "professionnel" && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="field-label">Raison sociale</label>
                  <input required className="field-input" value={billing.company_name} onChange={set("company_name")} data-testid="bill-company" />
                </div>
                <div>
                  <label className="field-label">Prénom du contact</label>
                  <input required className="field-input" value={billing.contact_first_name} onChange={set("contact_first_name")} data-testid="bill-contact-first" />
                </div>
                <div>
                  <label className="field-label">Nom du contact</label>
                  <input required className="field-input" value={billing.contact_last_name} onChange={set("contact_last_name")} data-testid="bill-contact-last" />
                </div>
                <div>
                  <label className="field-label">SIREN</label>
                  <input required className="field-input" value={billing.siren} onChange={set("siren")} data-testid="bill-siren" />
                </div>
                <div>
                  <label className="field-label">N° TVA (optionnel)</label>
                  <input className="field-input" value={billing.vat_number} onChange={set("vat_number")} data-testid="bill-vat" />
                </div>
              </div>
            )}

            {type === "particulier" && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Prénom</label>
                  <input required className="field-input" value={billing.first_name} onChange={set("first_name")} data-testid="bill-first" />
                </div>
                <div>
                  <label className="field-label">Nom</label>
                  <input required className="field-input" value={billing.last_name} onChange={set("last_name")} data-testid="bill-last" />
                </div>
              </div>
            )}

            <div>
              <label className="field-label">Email</label>
              <input required type="email" className="field-input" value={billing.email} onChange={set("email")} data-testid="bill-email" />
            </div>
            <div>
              <label className="field-label">Adresse</label>
              <input required className="field-input" value={billing.billing_address} onChange={set("billing_address")} data-testid="bill-address" />
            </div>
            <div>
              <label className="field-label">Complément d'adresse (optionnel)</label>
              <input className="field-input" value={billing.address_complement} onChange={set("address_complement")} data-testid="bill-complement" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="field-label">Code postal</label>
                <input required className="field-input" value={billing.postal_code} onChange={set("postal_code")} data-testid="bill-postal" />
              </div>
              <div>
                <label className="field-label">Ville</label>
                <input required className="field-input" value={billing.city} onChange={set("city")} data-testid="bill-city" />
              </div>
              <div>
                <label className="field-label">Pays</label>
                <input required className="field-input" value={billing.country} onChange={set("country")} data-testid="bill-country" />
              </div>
            </div>
          </div>

          {/* Warning */}
          <MedicalWarning
            compact
            text="Prestation de spiritualité, d'introspection et de bien-être. Elle ne constitue ni un acte médical, ni un diagnostic, ni un traitement, et ne remplace jamais un professionnel de santé."
          />

          {/* Consents */}
          <div className="card-soft p-6 space-y-4">
            {CONSENTS.map((c) => (
              <label key={c.key} className="flex items-start gap-3 cursor-pointer" data-testid={`consent-${c.key}`}>
                <input
                  type="checkbox"
                  checked={consents[c.key]}
                  onChange={(e) => setConsents({ ...consents, [c.key]: e.target.checked })}
                  className="mt-1 w-5 h-5 accent-[#5A3A4B] shrink-0"
                />
                <span className="text-sm text-[#7A5D58] leading-relaxed">
                  {c.key === "cgv" ? (
                    <>
                      J'accepte les{" "}
                      <Link to="/cgv" target="_blank" className="text-[#5A3A4B] underline">Conditions Générales de Vente</Link>.
                    </>
                  ) : (
                    c.label
                  )}
                </span>
              </label>
            ))}
          </div>

          <button type="submit" disabled={loading || !allConsents} className="btn-primary w-full" data-testid="checkout-pay-btn">
            <Lock size={16} /> {loading ? "Redirection…" : "Commander avec obligation de paiement"}
          </button>
        </form>

        {/* Summary */}
        <div className="lg:col-span-2">
          <div className="card-soft p-6 lg:sticky lg:top-28">
            <div className="eyebrow mb-3">Récapitulatif</div>
            <h3 className="font-serif text-2xl font-semibold text-[#5A3A4B]">{service.name}</h3>
            <p className="text-sm text-[#7A5D58] mt-1">{service.subtitle}</p>
            <div className="gold-divider my-5" />
            <div className="flex justify-between items-center">
              <span className="text-[#7A5D58]">Total</span>
              <span className="font-serif text-3xl font-semibold text-[#5A3A4B]">{eur(service.price_cents)}</span>
            </div>
            <ul className="mt-5 space-y-2 text-xs text-[#7A5D58]">
              <li>• Séance réalisée à distance</li>
              <li>• Aucun appel · aucune visioconférence</li>
              {service.duration_minutes ? <li>• Durée : {service.duration_minutes} minutes</li> : null}
              <li>• {service.slug === "sos-sorciere" ? "Réponse sous 24 h maximum" : "Créneau réservé après le paiement si nécessaire"}</li>
              <li>• Compte rendu par WhatsApp ou e-mail, selon ta préférence</li>
            </ul>
            <div className="mt-5 flex items-center gap-2 text-xs text-[#7A5D58]">
              <ShieldCheck size={16} className="text-[#C9A56A]" /> Paiement sécurisé Stripe
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
